/**
 * Dettaglio articolo — **MaeDesign**.
 *
 * Storia breve, perché spiega le scelte. La prima versione era Mantine nudo (`Stack maw={840}`,
 * Card e Badge di default). La seconda ha portato canvas, card e chip in tinta, ma teneva tre
 * cose fuori dal sistema — e sono esattamente quelle che facevano sembrare la pagina «diversa
 * dalle altre» anche a colpo d'occhio:
 *
 *  1. **nessun `MaePageHead`**: il titolo era un `<h1>` in mezzo al contenuto e le azioni
 *     (segnalibro, condividi) stavano a metà pagina accanto all'autore. Ogni altra pagina
 *     dell'app si apre con icon-box + titolo + azioni a destra; qui l'occhio non trovava
 *     l'ancora, e le due azioni erano invisibili finché non ci si inciampava;
 *  2. **`ActionIcon color="yellow"/"gray"`**: palette Mantine grezza, cioè la mappa colori
 *     locale che il design system vieta — MaeDesign non usa colori pieni ma tinta + testo forte;
 *  3. **`Typography`**: la tipografia di default di Mantine sul corpo
 *     dell'articolo. Ora il corpo passa da `RichTextView`, lo stesso blocco che rende le
 *     descrizioni scritte con l'editor: un testo scritto dagli utenti si legge allo stesso modo
 *     in tutta l'app, e le regole di contenimento (immagini, tabelle, `iframe`, `pre`) vivono in
 *     un posto solo invece di essere ricopiate qui.
 *
 * Via anche la shell locale: `MaeShell` è responsive di suo, quindi il ramo
 * «se mobile un div con padding mio» era una terza copia delle stesse misure.
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Anchor, Group, Stack, Text, rem } from '@mantine/core';
import {
  IconArrowLeft, IconBook, IconBookmark, IconBookmarkFilled, IconCalendar, IconClock, IconShare, IconTag, IconUser,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { fetchOne } from '@/shared/api/jsonapi/client';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageLoader } from '@/shared/components/LoadingState';
import { RichTextView } from '@/shared/components/RichTextView';
import { MaeCard, MaePageHead, MaeShell } from '@/shared/desktop/MaeShell';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { mw } from '@/shared/mobile/tokens';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

import { STORAGE_KEYS } from '@/shared/lib/constants';
import { useUserPreference } from '@/shared/lib/usePreferenzaUtente';

interface Article extends Denormalized {
  type: 'articles';
  title?: string;
  excerpt?: string;
  body?: string;
  category?: string;
  publishedAt?: string;
  authorName?: string;
  tags?: string | string[];   // il BE espone Tags come CSV; tolleriamo anche array
  coverUrl?: string;
}

export const Route = createFileRoute('/_app/articles_/$id')({ component: ArticleDetail });

/** Tinte MaeDesign per categoria (tint + testo forte, mai colori pieni). */
const CATEGORY_TONE: Record<string, { bg: string; fg: string }> = {
  guide: { bg: mw.accentTint, fg: mw.accentText },
  'novità': { bg: mw.brandTint, fg: mw.brand },
  policy: { bg: mw.warningBg, fg: mw.warning },
  faq: { bg: mw.successBg, fg: mw.success },
  tecnico: { bg: mw.surface2, fg: mw.ink500 },
};
function categoryTone(cat?: string) {
  return CATEGORY_TONE[(cat ?? '').toLowerCase()] ?? { bg: mw.surface2, fg: mw.ink500 };
}

const BOOKMARKS_KEY = STORAGE_KEYS.articlesBookmarks;

function readingTime(body?: string, excerpt?: string): number {
  const text = (body ?? excerpt ?? '').replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return 1;
  return Math.max(1, Math.round(words / 220));
}

/** Chip meta (icona + testo) della riga informazioni. */
function Meta({ icon: Icon, children }: { icon: typeof IconClock; children: React.ReactNode }) {
  return (
    <Group gap={5} wrap="nowrap" style={{ minWidth: 0 }}>
      <Icon size={14} style={{ color: mw.ink500, flexShrink: 0 }} />
      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }} lineClamp={1}>{children}</Text>
    </Group>
  );
}

/**
 * Azione di testata in stile MaeDesign: bordo freddo, tinta brand quando è attiva.
 * Sostituisce `ActionIcon color="yellow"`, che portava la palette Mantine dentro una pagina
 * che per il resto usa solo i token del brand.
 */
function HeaderAction({ label, active, onClick, children }: {
  label: string; active?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className="mw-pressable"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        height: rem(38), padding: '0 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 'var(--fs-compact)', fontWeight: 600,
        border: `1px solid ${active ? mw.brand : mw.line}`,
        background: active ? mw.brandTint : mw.surface,
        color: active ? mw.brand : mw.ink500,
      }}
    >
      {children}
    </button>
  );
}

function ArticleDetail() {
  const { id } = useParams({ from: '/_app/articles_/$id' });
  const isMobile = useIsMobileShell();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['article', id],
    queryFn: () => fetchOne<Article>('articles', id),
  });

  /* I segnalibri sono della persona: chi ne mette uno lo ritrova sul telefono. */
  const { value: bookmarks, write: writeBookmarks } = useUserPreference<string[]>(BOOKMARKS_KEY, []);
  const bookmarked = bookmarks.includes(id);

  const toggleBookmark = () => {
    writeBookmarks(bookmarked ? bookmarks.filter((x) => x !== id) : [...bookmarks, id]);
  };

  const share = async () => {
    const url = window.location.href;
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: data?.title ?? 'Articolo',
          url,
        });
        return;
      } catch { /* l'utente ha annullato */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      notifications.show({ color: 'green', message: 'Link copiato' });
    } catch {
      notifications.show({ color: 'orange', message: 'Impossibile copiare il link' });
    }
  };

  if (isLoading) return <MaeShell><PageLoader label="Carico l'articolo…" /></MaeShell>;

  if (isError || !data) {
    return (
      <MaeShell>
        <MaeCard>
          <Stack align="center" gap={10} py={24}>
            <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 15, background: mw.surface2, color: mw.ink300, alignItems: 'center', justifyContent: 'center' }}>
              <IconBook size={26} />
            </span>
            <Text style={{ fontSize: 'var(--fs-body)', color: mw.ink500 }}>Articolo non disponibile.</Text>
            <Anchor component={Link} to="/articles" style={{ color: mw.accentText, fontSize: 'var(--fs-compact)', fontWeight: 600 }}>Torna alla lista</Anchor>
          </Stack>
        </MaeCard>
      </MaeShell>
    );
  }

  const article = data;
  const minutes = readingTime(article.body, article.excerpt);
  const tone = categoryTone(article.category);
  // Tags: il BE espone Tags come CSV; tolleriamo string o array.
  const articleTags: string[] = Array.isArray(article.tags)
    ? article.tags.map((t) => String(t).trim()).filter(Boolean)
    : typeof article.tags === 'string'
      ? article.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

  return (
    <MaeShell>
      <div style={{ maxWidth: 880, marginInline: 'auto' }}>
        <Link to="/articles" style={{ textDecoration: 'none' }}>
          <Group gap={5} mb={14} style={{ color: mw.accentText, fontSize: 'var(--fs-compact)', fontWeight: 600 }}>
            <IconArrowLeft size={15} /> Tutti gli articoli
          </Group>
        </Link>

        {/* Testata di sistema: icon-box, titolo, sommario e le due azioni a destra — dove
            l'utente le cerca in ogni altra pagina, invece che a metà articolo. */}
        <MaePageHead
          title={article.title ?? '(senza titolo)'}
          sub={article.excerpt}
          icon={<IconBook size={23} />}
          actions={(
            <>
              <HeaderAction
                label={bookmarked ? 'Rimuovi dai segnalibri' : 'Aggiungi ai segnalibri'}
                active={bookmarked}
                onClick={toggleBookmark}
              >
                {bookmarked ? <IconBookmarkFilled size={16} /> : <IconBookmark size={16} />}
                {!isMobile && (bookmarked ? 'Salvato' : 'Salva')}
              </HeaderAction>
              <HeaderAction label="Condividi" onClick={() => void share()}>
                <IconShare size={16} />
                {!isMobile && 'Condividi'}
              </HeaderAction>
            </>
          )}
        />

        {/* Copertina: incorniciata come una card, non un'immagine appoggiata sul canvas. */}
        {article.coverUrl ? (
          <img
            src={article.coverUrl}
            alt=""
            loading="lazy"
            style={{
              display: 'block', width: '100%', maxHeight: 320, objectFit: 'cover',
              borderRadius: mw.radiusCard, border: `1px solid ${mw.line}`, marginBottom: 16,
            }}
          />
        ) : (
          <div style={{ height: 96, borderRadius: mw.radiusCard, marginBottom: 16, border: `1px solid ${mw.line}`, background: `linear-gradient(135deg, ${tone.bg}, ${mw.surface2})` }} />
        )}

        {/* Striscia informativa: categoria, tempo di lettura, autore, data. Una riga sola su
            desktop, a capo su mobile — `wrap` esplicito perché quattro voci in 375px non stanno. */}
        <MaeCard padding={isMobile ? 12 : '12px 18px'} style={{ marginBottom: 16 }}>
          <Group gap={16} wrap="wrap" align="center">
            {article.category && (
              <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: tone.bg, color: tone.fg }}>
                {article.category}
              </span>
            )}
            <Meta icon={IconClock}>{minutes} min di lettura</Meta>
            <Meta icon={IconUser}>{article.authorName ?? '—'}</Meta>
            {article.publishedAt && <Meta icon={IconCalendar}>{dayjs(article.publishedAt).format('DD MMM YYYY')}</Meta>}
          </Group>
        </MaeCard>

        <MaeCard padding={isMobile ? 16 : 24}>
          {article.body?.trim() ? (
            /* `RichTextView` ripulisce l'HTML e applica la tipografia MaeDesign: la stessa resa
               di una descrizione scritta con l'editor, e le regole che tengono immagini,
               tabelle e video dentro la colonna stanno con lei, non qui. */
            <RichTextView value={article.body} fontSize={isMobile ? 'var(--fs-body-lg)' : 'var(--fs-lead)'} />
          ) : (
            <EmptyState title="Nessun contenuto" description="L'articolo è ancora in bozza." py="md" />
          )}
        </MaeCard>

        {articleTags.length > 0 && (
          <Group gap={6} mt={16} wrap="wrap" align="center">
            <IconTag size={14} style={{ color: mw.ink300 }} />
            {articleTags.map((t) => (
              <span key={t} style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: mw.surface2, color: mw.ink500 }}>
                {t}
              </span>
            ))}
          </Group>
        )}
      </div>
    </MaeShell>
  );
}
